import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { PositionedEvent } from '@repo/types/calendar';
import type { MeetingPublic } from '@repo/types/meetings';

import { useCurrentTimeIndicator } from '@repo/hooks/useCurrentTimeIndicator';
import { cn } from '@repo/lib/cn';
import { CalendarViewCellHeight } from '@repo/lib/constants/calendar-constants';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { addHours, areIntervalsOverlapping, differenceInMinutes, eachHourOfInterval, format, getHours, getMinutes, isSameDay, startOfDay } from 'date-fns';
import { useEffect, useMemo } from 'react';

import { DraggableEvent, DroppableCell, EventItem } from '@/components/features/calendar';
import { Button } from '@/components/ui/button';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';
import { getCalendarEventUniqueId, type CalendarEvent, isHolidayCalendarEvent, isMultiDayEvent } from '@/utils/calendar';

import { useCalendarEvents } from './useCalendarEvents';

interface DayViewProps {
  rangeDates: EventCalendarRangeDates;
  currentDate: Date;
  onEventSelect: (event: MeetingPublic) => void;
  onEventCreate: (event: Date) => void;
}

export function DayView(dayViewProps: DayViewProps) {
  const { rangeDates, currentDate, onEventSelect, onEventCreate } = dayViewProps;

  const { lvl } = useCredentials();
  const lvlDisabled = useMemo<boolean>(() => lvl <= 0, [lvl]);

  const hours = useMemo(() => {
    const dayStart = startOfDay(currentDate);
    return eachHourOfInterval({
      start: dayStart,
      end: addHours(dayStart, 23),
    });
  }, [currentDate]);

  const { events, isError, error, refetch } = useCalendarEvents(rangeDates);

  const dayEvents = useMemo(() => {
    return events
      .filter((event) => {
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);
        return isSameDay(currentDate, eventStart) || isSameDay(currentDate, eventEnd) || (currentDate > eventStart && currentDate < eventEnd);
      })
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }, [currentDate, events]);

  // Filter all-day events
  const allDayEvents = useMemo(() => {
    return dayEvents.filter((event) => {
      // Include explicitly marked all-day events or multi-day events
      return event.allDay || isMultiDayEvent(event);
    });
  }, [dayEvents]);

  // Get only single-day time-based events
  const timeEvents = useMemo(() => {
    return dayEvents.filter((event) => {
      // Exclude all-day events and multi-day events
      return !event.allDay && !isMultiDayEvent(event);
    });
  }, [dayEvents]);

  // Process events to calculate positions
  const positionedEvents = useMemo<PositionedEvent<CalendarEvent>[]>(() => {
    const result: PositionedEvent<CalendarEvent>[] = [];
    const dayStart = startOfDay(currentDate);

    // Sort events by start time and duration
    const sortedEvents = [...timeEvents].sort((a, b) => {
      const aStart = new Date(a.startDate);
      const bStart = new Date(b.startDate);
      const aEnd = new Date(a.endDate);
      const bEnd = new Date(b.endDate);

      // First sort by start time
      if (aStart < bStart) return -1;
      if (aStart > bStart) return 1;

      // If start times are equal, sort by duration (longer events first)
      const aDuration = differenceInMinutes(aEnd, aStart);
      const bDuration = differenceInMinutes(bEnd, bStart);
      return bDuration - aDuration;
    });

    // Track columns for overlapping events
    const columns: { event: CalendarEvent; end: Date }[][] = [];

    sortedEvents.forEach((event) => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);

      // Adjust start and end times if they're outside this day
      const adjustedStart = isSameDay(currentDate, eventStart) ? eventStart : dayStart;
      const adjustedEnd = isSameDay(currentDate, eventEnd) ? eventEnd : addHours(dayStart, 24);

      // Calculate top position and height
      const startHour = getHours(adjustedStart) + getMinutes(adjustedStart) / 60;
      const endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60;
      const top = startHour * CalendarViewCellHeight;
      const height = (endHour - startHour) * CalendarViewCellHeight;

      // Find a column for this event
      let columnIndex = 0;
      let placed = false;

      while (!placed) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
          placed = true;
        } else {
          // Check if this event overlaps with any event in this column
          const overlaps = columns[columnIndex]?.some((col) =>
            areIntervalsOverlapping(
              { start: adjustedStart, end: adjustedEnd },
              {
                start: new Date(col.event.startDate),
                end: new Date(col.event.endDate),
              }
            )
          );

          if (!overlaps) {
            placed = true;
          } else {
            columnIndex++;
          }
        }
      }

      // Add event to its column
      columns[columnIndex]?.push({ event, end: adjustedEnd });

      // First column takes full width, others are indented by 10% and take 90% width
      const width = columnIndex === 0 ? 1 : Math.max(1 - columnIndex * 0.1, 0.4);
      const left = columnIndex === 0 ? 0 : columnIndex * 0.1;

      result.push({
        event,
        top,
        height,
        left,
        width,
        zIndex: 10 + columnIndex, // Higher columns get higher z-index
      });
    });

    return result;
  }, [currentDate, timeEvents]);

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isHolidayCalendarEvent(event)) return;
    onEventSelect(event);
  };

  const showAllDaySection = allDayEvents.length > 0;
  const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(currentDate, VIEW_KEY.DAY);

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error, {
        action: (
          <Button
            size={'sm'}
            className={'ml-auto'}
            variant={'tertiary'}
            onClick={() => {
              void refetch();
            }}
          >
            Ponów
          </Button>
        ),
      });
    }
  }, [error, isError, refetch]);

  return (
    <>
      {showAllDaySection && (
        <div className="border-border-primary/70 bg-surface-base/70 border-t">
          <div className="grid grid-cols-[3rem_1fr] sm:grid-cols-[4rem_1fr]">
            <div className="relative">
              <span className="text-text-tertiary absolute bottom-0 left-0 h-6 w-16 max-w-full pe-2 text-right text-[10px] sm:pe-4 sm:text-xs">C. d.</span>
            </div>
            <div className="border-border-primary/70 relative border-r p-1 last:border-r-0">
              {allDayEvents.map((event) => {
                const eventStart = new Date(event.startDate);
                const eventEnd = new Date(event.endDate);
                const isFirstDay = isSameDay(currentDate, eventStart);
                const isLastDay = isSameDay(currentDate, eventEnd);

                return (
                  <EventItem
                    key={`spanning-${getCalendarEventUniqueId(event)}`}
                    onClick={(e) => handleEventClick(event, e)}
                    event={event}
                    view={VIEW_KEY.MONTH}
                    isFirstDay={isFirstDay}
                    isLastDay={isLastDay}
                  >
                    {/* Always show the name in day view for better usability */}
                    <div>{event.name}</div>
                  </EventItem>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="border-border-primary/70 grid flex-1 grid-cols-[3rem_1fr] border-t sm:grid-cols-[4rem_1fr]">
        <div>
          {hours.map((hour, index) => (
            <div key={hour.toString()} className="border-border-primary/70 relative h-(--week-cells-height) border-b last:border-b-0">
              {index > 0 && (
                <span className="text-muted-foreground bg-background-dark md:bg-background absolute -top-3 left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs">
                  {format(hour, 'HH:mm')}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="relative">
          {/* Positioned events */}
          {positionedEvents.map((positionedEvent) => (
            <div
              key={getCalendarEventUniqueId(positionedEvent.event)}
              className="absolute z-10 px-0.5"
              style={{
                top: `${positionedEvent.top}px`,
                height: `${positionedEvent.height}px`,
                left: `${positionedEvent.left * 100}%`,
                width: `${positionedEvent.width * 100}%`,
                zIndex: positionedEvent.zIndex,
              }}
            >
              <div className="h-full w-full">
                <DraggableEvent
                  event={positionedEvent.event}
                  view={VIEW_KEY.DAY}
                  disabled={lvlDisabled || isHolidayCalendarEvent(positionedEvent.event)}
                  onClick={(e) => handleEventClick(positionedEvent.event, e)}
                  showTime
                  height={positionedEvent.height}
                />
              </div>
            </div>
          ))}

          {/* Current time indicator */}
          {currentTimeVisible && (
            <div className="pointer-events-none absolute right-0 left-0 z-20" style={{ top: `${currentTimePosition}%` }}>
              <div className="relative flex items-center">
                <div className="bg-brand absolute -left-1 h-2 w-2 rounded-full" />
                <div className="bg-brand h-[2px] w-full" />
              </div>
            </div>
          )}

          {/* Time grid */}
          {hours.map((hour) => {
            const hourValue = getHours(hour);
            return (
              <div key={hour.toString()} className="border-border-primary/70 relative h-(--week-cells-height) border-b last:border-b-0">
                {/* Quarter-hour intervals */}
                {[0, 1, 2, 3].map((quarter) => {
                  const quarterHourTime = hourValue + quarter * 0.25;
                  return (
                    <DroppableCell
                      key={`${hour.toString()}-${quarter}`}
                      id={`day-cell-${currentDate.toISOString()}-${quarterHourTime}`}
                      date={currentDate}
                      disabled={lvlDisabled}
                      time={quarterHourTime}
                      className={cn(
                        'absolute h-[calc(var(--week-cells-height)/4)] w-full',
                        quarter === 0 && 'top-0',
                        quarter === 1 && 'top-[calc(var(--week-cells-height)/4)]',
                        quarter === 2 && 'top-[calc(var(--week-cells-height)/4*2)]',
                        quarter === 3 && 'top-[calc(var(--week-cells-height)/4*3)]'
                      )}
                      onClick={() => {
                        const startTime = new Date(currentDate);
                        startTime.setHours(hourValue);
                        startTime.setMinutes(quarter * 15);
                        startTime.setSeconds(0);
                        startTime.setMilliseconds(0);
                        onEventCreate(startTime);
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
